import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Building2, Briefcase, UsersRound, Landmark, CreditCard } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { readPendingPlan, clearPendingPlan, describePendingPlan } from "@/lib/pendingPlan";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const initParams = new URLSearchParams(window.location.search);
  const initRole = ["founder", "gp", "investor"].includes(initParams.get("role")) ? initParams.get("role") : "founder";
  const [role, setRole] = useState(initRole);
  const [form, setForm] = useState({ name: "", email: initParams.get("email") || "", password: "", company_name: "" });
  const [loading, setLoading] = useState(false);
  const [invitedCompany, setInvitedCompany] = useState(null);
  const [pendingPlan] = useState(readPendingPlan);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (role === "investor" || !form.email.includes("@")) { setInvitedCompany(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/invite-check`, { params: { email: form.email } });
        setInvitedCompany(data.invited ? data.company_name : null);
      } catch { setInvitedCompany(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.email, role]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const actualRole = role === "investor" ? "investor" : "founder";
    try {
      const user = await register({ ...form, role: actualRole, org_type: role === "gp" ? "gp" : "company", lp_invite_token: initParams.get("invite") || undefined });
      toast.success(`Welcome, ${user.name}!`);
      const pendingRedeem = sessionStorage.getItem("tenx_redeem_code");
      if (actualRole === "founder" && !invitedCompany && !pendingRedeem) sessionStorage.setItem("tenx_choose_plan", "1");
      else clearPendingPlan();
      if (actualRole === "investor") {
        const redirect = sessionStorage.getItem("tenx_post_auth_redirect");
        if (redirect) navigate(redirect);
        else navigate("/portal");
      }
      else if (invitedCompany) navigate("/app");
      else if (pendingRedeem) navigate(`/redeem/${pendingRedeem}`);
      else navigate("/choose-plan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  const RoleCard = ({ value, icon: Icon, title, desc }) => (
    <button
      type="button"
      onClick={() => setRole(value)}
      data-testid={`role-${value}`}
      className={`flex-1 rounded-xl border p-3 text-left transition-colors ${role === value ? "border-primary bg-primary/5 ring-1 ring-primary" : "bg-card hover:bg-secondary"}`}
    >
      <Icon className={`mb-1.5 h-5 w-5 ${role === value ? "text-primary" : "text-muted-foreground"}`} />
      <div className="text-sm font-semibold text-foreground">{title}</div>
      <div className="text-xs text-muted-foreground">{desc}</div>
    </button>
  );

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Logo />
        <h1 className="h-display mt-8 text-2xl font-semibold text-foreground">Create your account</h1>
        <p className="mt-1 text-sm text-muted-foreground">Start sharing transparent updates in minutes.</p>

        <div className="mt-5 flex gap-3">
          <RoleCard value="founder" icon={Building2} title="Founder" desc="Create & send updates" />
          <RoleCard value="gp" icon={Landmark} title="Fund Manager" desc="Run funds & SPVs" />
          <RoleCard value="investor" icon={Briefcase} title="Investor" desc="View portfolio health" />
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          {role !== "investor" && pendingPlan && (
            <div className="flex items-start gap-2.5 rounded-xl border border-brand/40 bg-brand/10 p-3 text-sm text-foreground" data-testid="pending-plan-banner">
              <CreditCard className="mt-0.5 h-4 w-4 shrink-0 text-brand-ink" />
              <span>You picked <b>{describePendingPlan(pendingPlan)}</b> on Pricing. Create your account and confirm it on the next step.</span>
            </div>
          )}
          {role !== "investor" && invitedCompany && (
            <div className="flex items-start gap-2.5 rounded-xl border border-primary/30 bg-primary/5 p-3 text-sm text-foreground" data-testid="invite-banner">
              <UsersRound className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>You were invited to join <b>{invitedCompany}</b>. Completing signup adds you to their workspace.</span>
            </div>
          )}
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1" data-testid="register-name" />
          </div>
          {role !== "investor" && !invitedCompany && (
            <div>
              <Label htmlFor="company">{role === "gp" ? "Firm name" : "Company name"}</Label>
              <Input id="company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} required className="mt-1" placeholder={role === "gp" ? "Acme Capital" : "Acme Inc."} data-testid="register-company" />
            </div>
          )}
          <div>
            <Label htmlFor="remail">Email</Label>
            <Input id="remail" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="mt-1" data-testid="register-email" />
          </div>
          <div>
            <Label htmlFor="rpass">Password</Label>
            <Input id="rpass" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} className="mt-1" data-testid="register-password" />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
          </Button>
        </form>
        {role === "investor" && (
          <p className="mt-3 text-xs text-muted-foreground">Tip: use the same email your founders invited you with to see their companies.</p>
        )}
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account? <Link to="/login" className="font-medium text-primary hover:underline" data-testid="go-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
